import DesignTokens from '@tableau/design-tokens';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';
import { connect, Dispatch } from 'react-redux';
import { defaultMemoize } from 'reselect';
import _ from 'underscore';

import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { MapDispatchToProps, MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { CompositeSessionInfoSelectors, SessionServerSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Messages } from '~src/codegen/Localize';
import { Loadable, LoadingStatus } from '~src/httpClient/utils/Loadable';
import { useSettingsCoordinator } from '~src/settings/components/settings-coordinator/SettingsCoordinatorHook';
import { applyColor }from '~src/style/ColorService';
import { InlineCancelAndSaveButtons } from '~src/userSettings/components/UserSettingEditButtons';
import { buttonCell } from '~src/userSettings/styles/EmailAndDisplayNameFormStyles';
import { UserSettingsStyle } from '~src/userSettings/styles/UserSettingsStyle';

import { AppState } from '../../../bootstrap/redux/state/AppState';
import { IUserNotificationSetting, IUserNotificationSettingsInfo } from '../../models/IUserNotificationsSettingsInfo';
import { UserNotificationSettingsLayer } from '../../models/UserNotificationSettingsLayer';
import { UserNotificationSettingsActions } from '../../redux/UserNotificationSettingsActions';
import * as styles from '../../styles/userNotificationSettingsStyles';
import { UserNotificationsSettingsFormUtils } from './UserNotificationsSettingsFormUtils';
import { UserNotificationsSettingsGrid } from './UserNotificationsSettingsGrid';

export interface UserNotificationsSettingsFormPropsFromReduxDispatch {
  readonly actions: UserNotificationSettingsActions;
}

export interface UserNotificationsSettingsFormPropsFromReduxState {
  readonly settings: Loadable<IUserNotificationSettingsInfo>;
}

export interface UserNotificationsSettingsFormOwnProps {
  readonly layer: UserNotificationSettingsLayer;
  readonly siteId?: string;
}

export type UserNotificationsSettingsFormProps =
  UserNotificationsSettingsFormPropsFromReduxDispatch &
  UserNotificationsSettingsFormPropsFromReduxState &
  UserNotificationsSettingsFormOwnProps;

type FormFields = { [bucketAndChannel: string]: boolean };

export class UserNotificationsSettingsFormComponent extends React.PureComponent<UserNotificationsSettingsFormProps> {

  constructor(props: UserNotificationsSettingsFormProps) {
    super(props);
  }

  private async handleSubmit(values: FormFields, formik: FormikHelpers<FormFields>) {
    const settings: IUserNotificationSetting[] = _.keys(values)
      .map((key) => {
        const keyAttributes = UserNotificationsSettingsFormUtils.fromSettingKey(key);
        return {
          enabled: values[key],
          ...keyAttributes
        };
      })
      .filter(setting => _.isUndefined(_.findWhere(this.props.settings.content.settings, setting)));

    this.props.actions.updateSettings({ layer: this.props.layer, siteId: this.props.siteId, updatedSettings: settings });
  }

  componentDidMount() {
    this.props.actions.getSettings(this.props);
  }

  render() {
    if (this.props.settings.loadingStatus === LoadingStatus.FAILED) {
      return <LoadingFailedUI {...this.props} />;
    }
    if (this.props.settings.loadingStatus !== LoadingStatus.LOADED) {
      return null;
    }

    let settings = this.props.settings.content;

    const initialValues = this.getInitialValues(settings.settings);

    let layout = getLayout(this.props.layer);

    return (
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values: FormFields, formik: FormikHelpers<FormFields>) => {
          return this.handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<FormFields>) => (
          layout({
            formik: formik,
            settings: settings
          })
        )}
      </Formik>
    );
  }

  private getInitialValues(settings: IUserNotificationSetting[]): FormFields {
    let result: FormFields = {};
    settings.forEach(setting => {
      result[UserNotificationsSettingsFormUtils.toSettingKey(setting)] = setting.enabled;
    });
    return result;
  }
}

function SiteSettingsGrid(props: { settings: IUserNotificationSettingsInfo }) {
  // The form is a nested form on the site settings page, standalone on the user settings page...
  useSettingsCoordinator();
  return <UserNotificationsSettingsGrid settings={props.settings} />;
}

function getLayout(layer: UserNotificationSettingsLayer) {
  return layer === UserNotificationSettingsLayer.User
    ? MigratedUserFormLayout
    : SiteFormLayout;
}

function LoadingFailedUI(props: Pick<UserNotificationsSettingsFormProps, 'layer'>) {
  const layout = getLayout(props.layer);

  return layout({
    mainContent: (
      <div style={{ textAlign: 'center', color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.Grey77), margin: 28 }}>
        <div style={{ fontSize: 18, marginBottom: 28 }}>{Messages.userNotificationSettingsLoadError()}</div>
        <div style={{ fontSize: 14 }}>{Messages.userNotificationTryAgainLater()}</div>
      </div>
    ),
  });
}

interface LayoutProps {
  formik?: FormikProps<FormFields>;
  settings?: IUserNotificationSettingsInfo;
  mainContent?: JSX.Element;
}

function UserFormLayout(props: LayoutProps) {
  return (
    <table>
      <tbody>
        <tr>
          <td className='tb-details-value-multi-line'>
            <div className={styles.gridContainer}>
              {props.mainContent || (
                <>
                  <UserNotificationsSettingsGrid settings={props.settings} />
                  <ExplanationBlock />
                </>
              )}
            </div>
          </td>
          <td className={`tb-details-button-cell ${buttonCell} ${styles.buttonCell}`}>
            <ButtonWidget
              type='submit'
              buttonType={ButtonType.Outline}
              testId='user-notifications-settings-submit'
              disabled={!props.formik?.dirty || !props.formik?.isValid || props.formik.isSubmitting}
              onClick={props.formik?.submitForm}>
              {Messages.saveChanges()}
            </ButtonWidget>
          </td>
        </tr>
      </tbody>
    </table>
  );
}

function MigratedUserFormLayout(props: LayoutProps) {
  return (
    <div style={{ display: 'flex' }}>
      <div className={UserSettingsStyle.settingsLabelText}>
        <h4>{Messages.userSettings_Preference_Header()}</h4>
      </div>
      <div className={UserSettingsStyle.settingsLabelDescription}>
          <div className='tb-details-value-multi-line'>
            <div className={styles.gridContainerUserSettings}>
              {props.mainContent || (
                <>
                  <UserNotificationsSettingsGrid settings={props.settings} />
                  <ExplanationBlock />
                </>
              )}
            </div>
          </div>
        {
          showButtons(props.formik) && <>
            <InlineCancelAndSaveButtons
              testId={'user-notifications-settings'}
              formik={props.formik}
            />
          </>
        }
      </div>
    </div>
  );
}

function showButtons(formik: FormikProps<FormFields>): boolean {
  return (formik?.dirty && formik?.isValid && !formik?.isSubmitting);
}

const ExplanationBlock = () => <div className={styles.explanationBlock}>{Messages.userNotificationSettingsDisabledStateExplanation()}</div>;

const PulseSettingsDisclaimer = () => {
  // Only show for online servers and admin users
  const appState = getAppStore().getState();
  const isOnlineEnvironment = SessionServerSelectors.isOnlineEnvironment(appState);
  const isSiteOrServerAdmin = CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(appState);

  if (!isOnlineEnvironment || !isSiteOrServerAdmin) {
    return null;
  }

  const handleScrollToPulseSettings = () => {
    const pulseSettingsElement = document.querySelector('[data-tb-test-id="allow-pulse-feature-section"]');
    if (pulseSettingsElement) {
      pulseSettingsElement.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className={styles.pulseDisclaimerBlock} data-tb-test-id="pulse-settings-disclaimer">
      {Messages.userNotificationSettingsPulseDisclaimer()}
      <button
        type="button"
        onClick={handleScrollToPulseSettings}
        data-tb-test-id="pulse-settings-disclaimer-link"
        className={styles.pulseDisclaimerButton}
      >
        {Messages.userNotificationSettingsPulseDisclaimerLink()}
      </button>
    </div>
  );
};

function SiteFormLayout(props: LayoutProps) {
  return (
    <div className={styles.siteSettingsFormContainer}>
      <div className={styles.gridContainer}>
        {props.mainContent || (
          <>
            <SiteSettingsGrid settings={props.settings} />
            <ExplanationBlock />
            <PulseSettingsDisclaimer />
          </>
        )}
      </div>
    </div>
  );
}

export namespace ReduxConnectedUserNoficationsSettingsForm {

  const mapStateToProps: MapStateToProps<UserNotificationsSettingsFormPropsFromReduxState> = (state: AppState) => ({
    settings: state.notificationSettingsState?.settings
  });

  const mapDispatchToProps: MapDispatchToProps<UserNotificationsSettingsFormPropsFromReduxDispatch> = (dispatch: Dispatch<AppState>) => ({
    actions: memoizedActions(dispatch),
  });

  const memoizedActions = defaultMemoize((dispatch: Dispatch<AppState>) => new UserNotificationSettingsActions(dispatch));
  export const HOC = connect(mapStateToProps, mapDispatchToProps);
}

export const UserNotificationSettingsForm: React.ComponentType<UserNotificationsSettingsFormOwnProps> = ReduxConnectedUserNoficationsSettingsForm.HOC(UserNotificationsSettingsFormComponent);
